if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          mediaGallery: "[id^=Media-Gallery]",
          liveRegion: '[id^="GalleryStatus"]',
          viewer: '[id^="GalleryViewer"]',
          thumbnailsWrapper: ".f-product__media-thumbnails-wrapper",
          thumbnails: '[id^="GalleryThumbnails"]',
          thumbnailItems: ['[id^="GalleryThumbnailItem"]'],
          videos: [".f-video__embed"],
          models: ["product-model"],
          medias: [".f-product__media"],
          xrButton: "[data-first-xr-button]",
          sliderCounter: ".flickity-counter--current",
          toggleZoom: [".js-photoswipe--zoom"],
          variantNameDisplay: ".f-product__media-variant-name",
        };
        // Flag to prevent infinite loop when syncing carousel <-> variant
        this.isSelectingVariantFromSlide = false;
        this.isSelectingMediaFromVariant = false;

        let touchingCarousel = false;
        let startXCoordinate;

        document.body.addEventListener(
          "touchstart",
          (e) => {
            if (e.target.closest(".flickity-slider")) {
              touchingCarousel = true;
            } else {
              touchingCarousel = false;
              return;
            }
            startXCoordinate = e.touches[0].pageX;
          },
          { passive: true }
        );

        document.body.addEventListener(
          "touchmove",
          (e) => {
            if (touchingCarousel && e.cancelable) {
              if (Math.abs(e.touches[0].pageX - startXCoordinate) > 10) {
                e.preventDefault();
              }
            }
          },
          { passive: false }
        );
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        clearInterval(this.check);
      }

      getSelectedVariant() {
        const selectedVariant = this.querySelector(
          "[data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      init() {
        this.domNodes = queryDomNodes(this.selectors, this);
        this.mediaLayout = this.domNodes.mediaGallery.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === "true";
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest("product-info");
        this.enableZoom = this.dataset.enableZoom === "true";
        this.enableVariantGroupImages =
          this.dataset.enableVariantGroupImages === "true";
        this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true";
        // Check if we arrived via URL with variant parameter - if so, always show variant image
        this.hasVariantInUrl = new URLSearchParams(window.location.search).has('variant');
        this.variantPickers = this.section.querySelector(
          "[data-variant-picker]"
        );
        this.disableDefaultVariant =
          this.variantPickers &&
          this.variantPickers.dataset.disableDefaultVariant === "true";
        this.variantData = this.getVariantData();
        const selectedVariantId =
          (this.section &&
            this.section.querySelector('input[name="id"]')?.value) ||
          this.getSelectedVariant();
        this.currentVariant = this.variantData.find(
          (variant) => variant.id === Number(selectedVariantId)
        );

        // Build media to variant map for carousel-to-variant sync
        this.buildMediaToVariantMap();

        // Initialize variant name display element
        this.variantNameDisplay = this.querySelector(this.selectors.variantNameDisplay);

        if (!this.enableVariantGroupImages || this.onlyImage) {
          setTimeout(() => {
            this.removeAttribute("data-media-loading");
          }, 100);
        }
        if (this.mediaLayout === "carousel") {
          setTimeout(() => {
            this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
          });
          this.initGallerySlider();
        } else {
          const initSliders = () => {
            this.domNodes.mediaGallery &&
              this.domNodes.mediaGallery.initSlider();
            this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
            this.mediaGallery = this.domNodes.mediaGallery.slider.instance;
            this.thumbnails =
              this.domNodes.thumbnails &&
              this.domNodes.thumbnails.slider.instance;
            this.mediaGallery.on("change", this.onSlideChanged.bind(this));
            const variantMediaId = this.currentVariant?.featured_media?.id ||
                                   this.currentVariant?.featured_image?.id;
            if (
              this.currentVariant &&
              variantMediaId &&
              !this.disableDefaultVariant &&
              (!this.showFeaturedMedia || this.hasVariantInUrl)
            ) {
              this.setActiveMedia(variantMediaId);
            }
          };
          if (FoxThemeSettings.isMobile) {
            initSliders();
          }
          document.addEventListener("matchMobile", initSliders);
          document.addEventListener("unmatchMobile", () => {
            this.domNodes.mediaGallery && this.domNodes.mediaGallery.destroy();
            this.domNodes.thumbnails && this.domNodes.thumbnails.destroy();
            const variantMediaId = this.currentVariant?.featured_media?.id ||
                                   this.currentVariant?.featured_image?.id;
            if (
              this.currentVariant &&
              variantMediaId &&
              !this.disableDefaultVariant &&
              (!this.showFeaturedMedia || this.hasVariantInUrl)
            ) {
              this.setActiveMedia(variantMediaId);
            }
          });
          if (this.enableZoom) this.initImageZoom();
        }
        if (this.onlyImage && this.enableZoom) this.initImageZoom();

        setTimeout(() => {
          // Update initial variant name display
          if (this.currentVariant) {
            const mediaId = this.currentVariant.featured_media?.id || this.currentVariant.featured_image?.id;
            if (mediaId) {
              this.updateVariantNameDisplay(mediaId);
            }
          }

          this.dispatchEvent(
            new CustomEvent("product-media:loaded", {
              bubbles: true,
              detail: {
                currentVariant: this.currentVariant,
              },
            })
          );
        }, 100);
      }

      update() {
        if (this.domNodes.mediaGallery) this.domNodes.mediaGallery.update();
        if (this.domNodes.thumbnails) this.domNodes.thumbnails.update();
      }

      initGallerySlider() {
        if (!this.domNodes.medias.length) return;
        this.selectedSlide = null;
        this.selectedMediaType = "";
        this.check = setInterval(() => {
          this.mediaGallery =
            this.domNodes.mediaGallery.slider &&
            this.domNodes.mediaGallery.slider.instance;
          this.thumbnails =
            this.domNodes.thumbnails &&
            this.domNodes.thumbnails.slider &&
            this.domNodes.thumbnails.slider.instance;

          if (this.mediaGallery && typeof this.mediaGallery == "object") {
            clearInterval(this.check);
            this.mediaGallery.on("change", this.onSlideChanged.bind(this));

            if (this.thumbnails) {
              if (this.thumbnails.cells && this.thumbnails.cells[0]) {
                this.thumbnails.select(0);
                this.handleScreenChange();
              }
            }
            if (this.enableZoom) this.initImageZoom();
            // Get the media ID - try featured_media first, then featured_image
            const variantMediaId = this.currentVariant?.featured_media?.id ||
                                   this.currentVariant?.featured_image?.id;
            if (
              this.currentVariant &&
              variantMediaId &&
              !this.disableDefaultVariant &&
              (!this.showFeaturedMedia || this.hasVariantInUrl)
            ) {
              this.setActiveMedia(variantMediaId);
            }
          }
        }, 100);
      }

      getVariantData() {
        this.variantData =
          this.variantData ||
          JSON.parse(
            this.querySelector('[type="application/json"]').textContent
          );
        return this.variantData;
      }

      buildMediaToVariantMap() {
        // Create a map of mediaId -> variant for quick lookup
        this.mediaToVariantMap = new Map();
        if (!this.variantData) return;

        this.variantData.forEach((variant) => {
          const mediaId = variant.featured_media?.id || variant.featured_image?.id;
          if (mediaId) {
            // Only map if not already mapped (first variant wins)
            if (!this.mediaToVariantMap.has(String(mediaId))) {
              this.mediaToVariantMap.set(String(mediaId), variant);
            }
          }
        });
      }

      selectVariantFromMedia(mediaId) {
        // Prevent infinite loop
        if (this.isSelectingMediaFromVariant) return;

        const variant = this.mediaToVariantMap.get(String(mediaId));
        if (!variant || variant.id === this.currentVariant?.id) return;

        // Find the variant picker
        const variantPicker = this.section?.querySelector("variant-picker");
        if (!variantPicker) return;

        this.isSelectingVariantFromSlide = true;
        // Suppress focus-induced scroll for variant changes from carousel swipes.
        // We increment a counter instead of using a bool so overlapping swipes
        // don't clear the flag prematurely (slow section fetches can exceed
        // any fixed timeout window).
        window.__variantChangeFromCarousel = (window.__variantChangeFromCarousel || 0) + 1;
        // Keep the counter positive for a generous window (5 s) in case the
        // section fetch is slow on mobile networks. Each new swipe resets it.
        if (this._carouselFlagTimer) clearTimeout(this._carouselFlagTimer);
        this._carouselFlagTimer = setTimeout(() => {
          window.__variantChangeFromCarousel = 0;
        }, 5000);

        // Get all option groups by their field wrapper
        const optionGroups = variantPicker.querySelectorAll('.variant-picker__field-wrapper');
        const options = [variant.option1, variant.option2, variant.option3];

        options.forEach((optionValue, idx) => {
          if (!optionValue) return;

          const group = optionGroups[idx];
          if (!group) return;

          // Find radio button with matching value in this group
          const allRadios = group.querySelectorAll('input[type="radio"]');
          let radioInput = Array.from(allRadios).find(
            input => input.value.trim() === optionValue.trim()
          );

          if (radioInput && !radioInput.checked) {
            radioInput.checked = true;
            radioInput.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (!radioInput) {
            // Try select dropdown
            const selectBox = group.querySelector('select');
            if (selectBox) {
              const opt = Array.from(selectBox.options).find(
                o => o.value.trim() === optionValue.trim() || o.textContent.trim() === optionValue.trim()
              );
              if (opt && selectBox.value !== opt.value) {
                selectBox.value = opt.value;
                selectBox.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
        });

        // Update current variant reference
        this.currentVariant = variant;

        // Reset the per-instance loop-prevention flag; the carousel flag is
        // managed by the timer set above so it survives slow fetches.
        setTimeout(() => {
          this.isSelectingVariantFromSlide = false;
        }, 800);
      }

      updateVariantNameDisplay(mediaId) {
        if (!this.variantNameDisplay) return;

        const variant = this.mediaToVariantMap.get(String(mediaId));
        const spanElement = this.variantNameDisplay.querySelector('span');

        if (variant && spanElement) {
          // Combine option1 (material) and option2 (cannage) if both exist
          const parts = [variant.option1, variant.option2].filter(Boolean);
          spanElement.textContent = parts.join(' - ') || variant.title || '';
          this.variantNameDisplay.style.display = 'flex';
        } else if (spanElement) {
          this.variantNameDisplay.style.display = 'none';
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = this.querySelectorAll(".f-product__media");
        if (allMedia) {
          allMedia.forEach((media) => {
            if (media.dataset.mediaType === "image") {
              dataSource.push({
                id: media.dataset.mediaIndex,
                src: media.dataset.src,
                width: media.dataset.pswpWidth,
                height: media.dataset.pswpHeight,
                mediaType: media.dataset.mediaType,
              });
            }
            if (media.dataset.mediaType === "model") {
              const html = `<div class="pswp__item--${
                media.dataset.mediaType
              }">${media.querySelector("product-model").outerHTML}</div>`;
              dataSource.push({
                id: media.dataset.mediaIndex,
                html: html,
                mediaType: media.dataset.mediaType,
              });
            }

            if (
              media.dataset.mediaType === "video" ||
              media.dataset.mediaType == "external_video"
            ) {
              const html = `<div class="pswp__item--${
                media.dataset.mediaType
              }">${media.querySelector("deferred-media").outerHTML}</div>`;
              dataSource.push({
                id: media.dataset.mediaIndex,
                html: html,
                mediaType: media.dataset.mediaType,
              });
            }
          });
        }

        const options = {
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        };

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox(options);

        this.lightbox.addFilter("thumbEl", (thumbEl, data, index) => {
          const el = this.querySelector(
            '[data-media-index="' + data.id + '"] img'
          );
          if (el) {
            return el;
          }
          return thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, slide) => {
          const el = this.querySelector(
            '[data-media-index="' + slide.data.id + '"] img'
          );
          if (el) {
            return el.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on("change", () => {
          const currIndex = this.lightbox.pswp.currIndex;
          if (!this.onlyImage) {
            const slider =
              this.domNodes.mediaGallery.slider &&
              this.domNodes.mediaGallery.slider.instance;
            if (slider) {
              setTimeout(() => {
                slider.select(currIndex, false, true);
              }, 300);
            }
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          const currSlide = this.lightbox.pswp.currSlide.data;
          if (
            currSlide.mediaType === "video" ||
            currSlide.mediaType === "model" ||
            currSlide.mediaType === "external_video"
          ) {
            e.preventDefault();
          }
        });

        this.lightbox.on("uiRegister", () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: "next",
              ariaLabel: "Next slide",
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next f-rlt-reverse-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180) "></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: "prev",
              ariaLabel: "Previous slide",
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev f-rlt-reverse-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }

          this.lightbox.pswp.ui.registerElement({
            name: "close-zoom",
            ariaLabel: "Close zoom image",
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="f-icon-svg f-icon--medium  f-icon-close"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        addEventDelegate({
          selector: this.selectors.toggleZoom[0],
          context: this,
          handler: (e, media) => {
            const isImage = media.dataset.mediaType === "image";
            if (isImage) {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      onSlideChanged(index) {
        this.draggable = true;
        this.selectedSlide = this.mediaGallery.selectedElement;
        this.selectedMediaType = this.selectedSlide.dataset.mediaType;
        this.selectedMediaId = this.selectedSlide.dataset.mediaId;

        const mediaAspectRatio = this.selectedSlide.dataset.aspectRatio;
        this.domNodes.viewer.style.setProperty(
          "--media-aspect-ratio",
          mediaAspectRatio
        );

        if (
          "model,external_video,video".includes(this.selectedMediaType) &&
          this.draggable
        ) {
          this.toggleDraggable();
        } else {
          this.toggleDraggable(true);
        }

        if (this.selectedMediaType === "model") {
          this.domNodes.xrButton &&
            this.domNodes.xrButton.setAttribute(
              "data-shopify-model3d-id",
              this.selectedMediaId
            );
        }

        if (this.thumbnails) {
          this.thumbnails.select(index);
        }

        if (this.domNodes.sliderCounter) {
          this.domNodes.sliderCounter.textContent = index + 1;
        }

        this.domNodes.mediaGallery.classList.remove("pointer-move");

        this.playActiveMedia(this.selectedSlide);

        // Sync carousel slide to variant selection
        if (this.selectedMediaId && !this.isSelectingMediaFromVariant) {
          this.selectVariantFromMedia(this.selectedMediaId);
        }

        // Update variant name display under image
        this.updateVariantNameDisplay(this.selectedMediaId);
      }

      handleScreenChange() {
        document.addEventListener("matchMobile", () => {
          this.domNodes.thumbnails.update();
          if (this.thumbnails.cells.length > 4) {
            this.thumbnails.options.wrapAround = true;
            this.domNodes.thumbnails.update();
          }
        });
        document.addEventListener("unmatchMobile", () => {
          this.domNodes.thumbnails.update();
          if (this.thumbnails.cells.length < 6) {
            this.thumbnails.options.wrapAround = false;
            this.domNodes.thumbnails.update();
          }
        });
        if (this.thumbnails.cells.length > 5) {
          this.domNodes.thumbnails.update();
          this.thumbnails.options.wrapAround = true;
          this.domNodes.thumbnails.update();
        }
      }

      toggleDraggable(status = false) {
        this.draggable = status;
        this.mediaGallery.options.draggable = status;
        this.mediaGallery.updateDraggable();
      }
      playActiveMedia(selected) {
        window.pauseAllMedia();
        const deferredMedia = selected.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(mediaId) {
        const selectedMedia = this.domNodes.mediaGallery.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        if (!selectedMedia) return;
        const mediaIndex = Number(selectedMedia.dataset.mediaIndex);

        // Set flag to prevent infinite loop (variant change -> media change -> variant change)
        this.isSelectingMediaFromVariant = true;

        if (this.mediaLayout === "carousel" || FoxThemeSettings.isMobile) {
          this.mediaGallery &&
            this.mediaGallery.select(mediaIndex, false, true);
        } else {
          this.scrollIntoView(selectedMedia);
        }

        // Update variant name display
        this.updateVariantNameDisplay(mediaId);

        // Reset flag after a short delay
        setTimeout(() => {
          this.isSelectingMediaFromVariant = false;
        }, 100);
      }

      scrollIntoView(selectedMedia) {
        if (FoxThemeSettings.isMobile) return;
        selectedMedia.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  );
}
